import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { RequestStatus } from '@freelancer/datastore';
import {
  ContestBudgetClassification,
  ContestBudgetRange,
  ContestBudgetRangesCollection,
  ContestMinimumBudgetClassification,
  CurrenciesCollection,
  Currency,
} from '@freelancer/datastore/collections';
import { HeadingType } from '@freelancer/ui/heading';
import { InputSize, InputType } from '@freelancer/ui/input';
import { Margin } from '@freelancer/ui/margin';
import { SelectItem, SelectSize } from '@freelancer/ui/select';
import { SliderLabelPosition } from '@freelancer/ui/slider';
import { SpinnerSize } from '@freelancer/ui/spinner';
import { TextAlign, TextSize } from '@freelancer/ui/text';
import * as Rx from 'rxjs';
import {
  distinctUntilChanged,
  map,
  publishReplay,
  refCount,
  startWith,
  takeWhile,
} from 'rxjs/operators';

@Component({
  selector: 'app-contest-budget',
  template: `
    <fl-bit class="SpinnerContainer" [flMarginBottom]="Margin.XXXSMALL">
      <fl-heading
        i18n="Post Job Page form contest budget field label"
        [headingType]="HeadingType.H3"
        [flMarginBottom]="Margin.XXXSMALL"
        [size]="TextSize.MID"
      >
        What is your budget?
      </fl-heading>
      <fl-spinner
        class="SpinnerContainer-spinner"
        *ngIf="showSpinner$ | async"
        flTrackingLabel="PJPContestBudgetInitialisationSpinner"
        [size]="SpinnerSize.SMALL"
      ></fl-spinner>
    </fl-bit>
    <fl-card [edgeToEdge]="true" [flMarginBottom]="Margin.LARGE">
      <app-contest-expected-entries
        [budgetClassification$]="budgetClassification$"
      ></app-contest-expected-entries>
      <fl-bit class="BudgetContainer">
        <fl-bit *ngIf="showSlider$ | async" [flMarginBottom]="Margin.LARGE">
          <fl-slider
            class="BudgetSlider"
            flTrackingLabel="Slider"
            [currencyCode]="(selectedCurrency$ | async)?.code"
            [flTrackingExtraParams]="{ value: contestBudgetControl.value }"
            [labelPosition]="SliderLabelPosition.NONE"
            [minValue]="(budgetRange$ | async)?.minimum"
            [maxValue]="(budgetRange$ | async)?.maximum"
            [maxControl]="contestBudgetControl"
          ></fl-slider>
          <fl-grid class="BudgetSliderLabel">
            <fl-col [col]="6">
              <fl-text [textAlign]="TextAlign.LEFT">
                {{
                  (budgetRange$ | async)?.minimum
                    | flCurrency: (selectedCurrency$ | async)?.code
                }}
              </fl-text>
            </fl-col>
            <fl-col [col]="6">
              <fl-text [textAlign]="TextAlign.RIGHT">
                {{
                  (budgetRange$ | async)?.maximum
                    | flCurrency: (selectedCurrency$ | async)?.code
                }}+
              </fl-text>
            </fl-col>
          </fl-grid>
        </fl-bit>
        <fl-bit>
          <fl-grid>
            <fl-col [col]="12" [colTablet]="6">
              <fl-input
                flTrackingLabel="BudgetInputField"
                [beforeLabel]="(selectedCurrency$ | async)?.sign"
                [control]="contestBudgetControl"
                [flMarginBottom]="Margin.XSMALL"
                [flTrackingExtraParams]="{ value: contestBudgetControl.value }"
                [size]="InputSize.LARGE"
                [type]="InputType.NUMBER"
              ></fl-input>
            </fl-col>
            <fl-col [col]="12" [colTablet]="6">
              <fl-select
                flTrackingLabel="SelectCurrency"
                flTrackingReferenceType="currency_id"
                [control]="currencyIdControl"
                [flTrackingReferenceId]="currencyIdControl?.value"
                [options]="currencyOptions$ | async"
                [size]="SelectSize.LARGE"
              ></fl-select>
            </fl-col>
          </fl-grid>
        </fl-bit>
      </fl-bit>
    </fl-card>
  `,
  styleUrls: ['./budget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContestBudgetComponent implements OnInit {
  HeadingType = HeadingType;
  InputSize = InputSize;
  InputType = InputType;
  Margin = Margin;
  SelectSize = SelectSize;
  SliderLabelPosition = SliderLabelPosition;
  SpinnerSize = SpinnerSize;
  TextAlign = TextAlign;
  TextSize = TextSize;

  @Input() budgetRange$: Rx.Observable<ContestBudgetRange>;
  @Input() budgetRangeStatus$: Rx.Observable<
    RequestStatus<ContestBudgetRangesCollection>
  >;
  @Input() contestBudgetControl: FormControl;
  @Input() contestMinimumBudgetClassification$: Rx.Observable<
    ContestMinimumBudgetClassification
  >;
  @Input() currenciesStatus$: Rx.Observable<
    RequestStatus<CurrenciesCollection>
  >;
  @Input() currencyIdControl: FormControl;
  @Input() currencyOptions$: Rx.Observable<ReadonlyArray<SelectItem>>;
  @Input() selectedCurrency$: Rx.Observable<Currency>;

  budgetClassification$: Rx.Observable<ContestBudgetClassification>;
  showSlider$: Rx.Observable<boolean>;
  showSpinner$: Rx.Observable<boolean>;

  ngOnInit() {
    const budget$: Rx.Observable<number> = this.contestBudgetControl.valueChanges.pipe(
      startWith(this.contestBudgetControl.value),
      publishReplay(1),
      refCount(),
    );

    this.budgetClassification$ = Rx.combineLatest([
      budget$,
      this.contestMinimumBudgetClassification$,
    ]).pipe(
      map(([budget, config]) => this.getBudgetClassification(budget, config)),
      distinctUntilChanged(),
    );

    this.showSlider$ = this.budgetRangeStatus$.pipe(
      map(status => status.ready && !status.error),
    );

    this.showSpinner$ = this.currenciesStatus$.pipe(
      map(status => !status.ready || !!status.error),
    );

    // Mark input as "touched" even when updated via slider
    this.contestBudgetControl.valueChanges
      .pipe(takeWhile(() => this.contestBudgetControl.untouched))
      .subscribe(() => this.contestBudgetControl.markAsTouched());
  }

  getBudgetClassification(
    budget: number,
    config: ContestMinimumBudgetClassification,
  ): ContestBudgetClassification {
    if (budget < config.good) {
      return ContestBudgetClassification.OK;
    }

    if (budget < config.great) {
      return ContestBudgetClassification.GOOD;
    }

    if (budget < config.excellent) {
      return ContestBudgetClassification.GREAT;
    }

    if (budget < config.best) {
      return ContestBudgetClassification.EXCELLENT;
    }

    if (budget < config.amazing) {
      return ContestBudgetClassification.BEST;
    }

    return ContestBudgetClassification.AMAZING;
  }
}
